// src/pages/onboarding/OnboardingChannelsPage.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "./OnboardingLayout";
import {
  BotChannel,
  ChannelType,
  fetchChannels,
  deleteChannel,
  getMetaConnectUrl,
  getMetaSession,
  attachMetaSession,
  MetaSessionResponse,
  completeWhatsappEmbeddedSignup,
  attachWhatsappSession,
  WhatsappConnectSessionResponse,
  updateBot
} from "../../api/bots";
import { loadFacebookSdk } from "../../utils/facebookSdk";

import {
  ALWAYS_ON_FEATURES,
  mapChannelsToFeatureFlags
} from "./onboardingFeatureHelpers";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function isMetaChannelType(type: ChannelType) {
  return type === "FACEBOOK" || type === "INSTAGRAM";
}

const OnboardingChannelsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [channels, setChannels] = useState<BotChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Feature toggles
  const [metaEnabled, setMetaEnabled] = useState<boolean>(true);
  const [whatsappEnabled, setWhatsappEnabled] = useState<boolean>(true);

  // Meta connect session state (page selection)
  const [metaSession, setMetaSession] = useState<MetaSessionResponse | null>(
    null
  );
  const [metaSessionLoading, setMetaSessionLoading] = useState(false);
  const [metaSessionError, setMetaSessionError] = useState<string | null>(null);
  const [metaSelectedPageId, setMetaSelectedPageId] = useState<string>("");
  const [metaAttachLoading, setMetaAttachLoading] = useState(false);
  const [metaSelectModalOpen, setMetaSelectModalOpen] = useState(false);

  // Meta permissions confirmation dialog
  const [metaPermissionsChannel, setMetaPermissionsChannel] = useState<
    "FACEBOOK" | "INSTAGRAM" | null
  >(null);
  const [metaPermissionsAccepted, setMetaPermissionsAccepted] =
    useState(false);
  const [metaPermissionsSubmitting, setMetaPermissionsSubmitting] =
    useState(false);

  // WhatsApp embedded signup session state
  const [waSession, setWaSession] =
    useState<WhatsappConnectSessionResponse | null>(null);
  const [waSelectedNumberId, setWaSelectedNumberId] = useState<string>("");
  const [waConnecting, setWaConnecting] = useState(false);
  const [waAttachLoading, setWaAttachLoading] = useState(false);
  const [waRegistrationPin, setWaRegistrationPin] = useState<string>("");

  const [saving, setSaving] = useState(false);

  const loadChannels = () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchChannels(id)
      .then((data) => setChannels(data))
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botChannels.errors.loadFailed"));
      })
      .finally(() => setLoading(false));
  };

  const clearMetaSessionFromUrl = () => {
    const params = new URLSearchParams(location.search);
    if (params.has("metaSessionId")) {
      params.delete("metaSessionId");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString()
        },
        { replace: true }
      );
    }
  };

  const loadMetaSessionFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("metaSessionId");
    if (!sessionId) {
      setMetaSession(null);
      setMetaSessionError(null);
      setMetaSelectedPageId("");
      setMetaSelectModalOpen(false);
      return;
    }

    setMetaSessionLoading(true);
    setMetaSessionError(null);
    getMetaSession(sessionId)
      .then((session) => {
        setMetaSession(session);
        setMetaSelectedPageId("");
        setMetaSelectModalOpen(true);
      })
      .catch((err: any) => {
        console.error(err);
        setMetaSessionError(
          err?.message || t("botChannels.errors.metaSessionLoad")
        );
        setMetaSession(null);
        setMetaSelectModalOpen(false);
      })
      .finally(() => setMetaSessionLoading(false));
  };

  useEffect(() => {
    loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadMetaSessionFromQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const openMetaPermissionsDialog = (channel: "FACEBOOK" | "INSTAGRAM") => {
    setMetaPermissionsChannel(channel);
    setMetaPermissionsAccepted(false);
    setMetaPermissionsSubmitting(false);
  };

  const closeMetaPermissionsDialog = () => {
    setMetaPermissionsChannel(null);
    setMetaPermissionsAccepted(false);
    setMetaPermissionsSubmitting(false);
  };

  const handleConfirmMetaPermissions = async () => {
    if (!id || !metaPermissionsChannel) return;

    setMetaPermissionsSubmitting(true);
    setError(null);

    try {
      const returnPath = `/onboarding/bots/${encodeURIComponent(id)}/channels`;
      const { url } = await getMetaConnectUrl(
        id,
        metaPermissionsChannel,
        returnPath
      );
      window.location.href = url;
    } catch (err: any) {
      console.error(err);
      const errorKey =
        metaPermissionsChannel === "FACEBOOK"
          ? "botChannels.errors.facebookConnectFailed"
          : "botChannels.errors.instagramConnectFailed";
      setError(err?.message || t(errorKey));
      setMetaPermissionsSubmitting(false);
    }
  };

  const closeMetaSelectModal = () => {
    setMetaSelectModalOpen(false);
    setMetaSession(null);
    setMetaSelectedPageId("");
    clearMetaSessionFromUrl();
  };

  const handleAttachMetaSession = async () => {
    if (!metaSession || !metaSelectedPageId) return;
    setMetaAttachLoading(true);
    setError(null);

    try {
      await attachMetaSession(metaSession.id, metaSelectedPageId);
      setMetaSession(null);
      setMetaSelectedPageId("");
      clearMetaSessionFromUrl();
      setMetaSelectModalOpen(false);
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.metaAttachFailed"));
    } finally {
      setMetaAttachLoading(false);
    }
  };

  const handleConnectWhatsApp = async () => {
    if (!id) return;
    setError(null);
    setWaConnecting(true);

    const appId =
      (import.meta as any).env.VITE_META_APP_ID ||
      (import.meta as any).env.VITE_FACEBOOK_APP_ID;
    const configId = (import.meta as any).env.VITE_WHATSAPP_EMBEDDED_CONFIG_ID;
    const redirectUri =
      (import.meta as any).env.VITE_WHATSAPP_EMBEDDED_REDIRECT_URI ||
      `${window.location.origin}/app/bots`;

    if (!appId || !configId) {
      setError(t("botChannels.errors.whatsappEnvMissing"));
      setWaConnecting(false);
      return;
    }

    try {
      const lastConnectTimestampStr =
        window.localStorage.getItem("wa_last_connect_ts");
      if (lastConnectTimestampStr) {
        const lastConnectTimestamp = parseInt(lastConnectTimestampStr, 10);
        if (Date.now() - lastConnectTimestamp < SEVEN_DAYS_MS) {
          if (
            !window.confirm(
              t(
                "botChannels.whatsapp.confirmReconnectWithinWeek",
                "You recently connected WhatsApp. Start a new embedded signup anyway?"
              )
            )
          ) {
            setWaConnecting(false);
            return;
          }
        }
      }

      const FB = await loadFacebookSdk(appId);

      FB.login(
        (response: any) => {
          (async () => {
            if (
              !response ||
              !response.authResponse ||
              !response.authResponse.code
            ) {
              setError(t("botChannels.errors.whatsappCancelled"));
              setWaConnecting(false);
              return;
            }

            const code = response.authResponse.code as string;

            try {
              const session = await completeWhatsappEmbeddedSignup(id, {
                code,
                redirectUri
              });
              setWaSession(session);
              setWaSelectedNumberId("");
              setWaRegistrationPin("");
            } catch (err: any) {
              console.error(err);
              setError(
                err?.message || t("botChannels.errors.whatsappSignupFailed")
              );
            } finally {
              setWaConnecting(false);
            }
          })();
        },
        {
          config_id: configId,
          redirect_uri: redirectUri,
          response_type: "code",
          override_default_response_type: true
        }
      );

      window.localStorage.setItem(
        "wa_last_connect_ts",
        Date.now().toString()
      );
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.whatsappInitFailed"));
      setWaConnecting(false);
    }
  };

  const handleAttachWhatsappSession = async () => {
    if (!waSession || !waSelectedNumberId) return;
    setWaAttachLoading(true);
    setError(null);

    try {
      await attachWhatsappSession(
        waSession.sessionId,
        waSelectedNumberId,
        waRegistrationPin || undefined
      );
      setWaSession(null);
      setWaSelectedNumberId("");
      setWaRegistrationPin("");
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.whatsappAttachFailed"));
    } finally {
      setWaAttachLoading(false);
    }
  };

  const handleDelete = async (channelId: string) => {
    if (!id) return;
    if (!window.confirm(t("botChannels.confirm.deleteChannel"))) return;

    setError(null);
    try {
      await deleteChannel(id, channelId);
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.deleteFailed"));
    }
  };

  const getChannelStatusInfo = (
    ch: BotChannel | null
  ): { label: string; className: string; isConnected: boolean } => {
    if (!ch) {
      return {
        label: t("botChannels.status.notConnected"),
        className: "status-badge newstatus-badge-warn",
        isConnected: false
      };
    }

    const meta = (ch.meta as any) || {};

    if (!isMetaChannelType(ch.type)) {
      const needsReconnect = meta.needsReconnect === true;
      if (needsReconnect) {
        return {
          label: t("botChannels.status.needsReconnect"),
          className: "status-badge newstatus-badge-error",
          isConnected: false
        };
      }
      return {
        label: t("botChannels.status.active"),
        className: "status-badge newstatus-badge-ok",
        isConnected: true
      };
    }

    const now = Date.now();
    const needsReconnect = meta.needsReconnect === true;
    const tokenExpiresAtStr: string | undefined = meta.tokenExpiresAt;
    let expiresAt: Date | null = null;

    if (tokenExpiresAtStr) {
      const d = new Date(tokenExpiresAtStr);
      if (!isNaN(d.getTime())) expiresAt = d;
    }

    if (needsReconnect) {
      return {
        label: t("botChannels.status.needsReconnect"),
        className: "status-badge newstatus-badge-error",
        isConnected: false
      };
    }

    if (!expiresAt) {
      return {
        label: t("botChannels.status.connectedUnknownExpiry"),
        className: "status-badge newstatus-badge-ok",
        isConnected: true
      };
    }

    const diff = expiresAt.getTime() - now;

    if (diff <= 0) {
      return {
        label: t("botChannels.status.expired"),
        className: "status-badge newstatus-badge-error",
        isConnected: false
      };
    }

    if (diff <= SEVEN_DAYS_MS) {
      return {
        label: t("botChannels.status.expiringSoon"),
        className: "status-badge newstatus-badge-warn",
        isConnected: false
      };
    }

    return {
      label: t("botChannels.status.connected"),
      className: "status-badge newstatus-badge-ok",
      isConnected: true
    };
  };

  const renderChannelStatusBadge = (ch: BotChannel | null) => {
    const { label, className } = getChannelStatusInfo(ch);
    return <span className={className}>{label}</span>;
  };

  const facebookChannel = channels.find((c) => c.type === "FACEBOOK") || null;
  const instagramChannel =
    channels.find((c) => c.type === "INSTAGRAM") || null;
  const whatsappChannel = channels.find((c) => c.type === "WHATSAPP") || null;

  const fbStatus = getChannelStatusInfo(facebookChannel);
  const igStatus = getChannelStatusInfo(instagramChannel);
  const waStatus = getChannelStatusInfo(whatsappChannel);

  // Derived "connected" states for the wizard guard
  const metaConnected =
    metaEnabled && (fbStatus.isConnected || igStatus.isConnected);
  const whatsappConnected = whatsappEnabled && waStatus.isConnected;
  const canContinue = metaConnected || whatsappConnected;

  const handleNext = async () => {
    if (!id || !canContinue) return;
    setSaving(true);
    try {
      const currentChannels = await fetchChannels(id);
      const channelFlags = mapChannelsToFeatureFlags(currentChannels);

      await updateBot(id, {
        ...ALWAYS_ON_FEATURES,
        // if the section is disabled, force flags off regardless of channels
        channelWhatsapp: whatsappEnabled ? channelFlags.channelWhatsapp : false,
        channelMessenger: metaEnabled ? channelFlags.channelMessenger : false,
        channelInstagram: metaEnabled ? channelFlags.channelInstagram : false
      });

      navigate(`/onboarding/bots/${encodeURIComponent(id)}/booking`);
    } catch (err) {
      console.error("Failed to save channel features", err);
      // optional: setError for user feedback
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await updateBot(id, {
        ...ALWAYS_ON_FEATURES,
        channelWhatsapp: false,
        channelMessenger: false,
        channelInstagram: false
      });

      navigate(`/onboarding/bots/${encodeURIComponent(id)}/booking`);
    } catch (err) {
      console.error("Failed to skip channels", err);
    } finally {
      setSaving(false);
    }
  };

  if (!id) {
    return (
      <OnboardingLayout
        currentStep="channels"
        title={t(
          "botChannels.onboarding.channels.title",
          "Where should Coslo talk with your customers?"
        )}
      >
        <p className="muted">{t("botChannels.missingId")}</p>
      </OnboardingLayout>
    );
  }

  const isValidWaPin = /^\d{6}$/.test(waRegistrationPin);

  return (
    <OnboardingLayout
      currentStep="channels"
      botId={id}
      title={t(
        "botChannels.onboarding.channels.title",
        "Where should Coslo talk with your customers?"
      )}
      subtitle={t(
        "botChannels.onboarding.channels.subtitle",
        "Connect at least one channel now. You can always add more later from the dashboard."
      )}
      layout="full"
    >
      <div className="page" style={{ margin: 0, padding: 0 }}>
        {/* Skip top-right */}
        <div className="onboarding-section-header">
          <button
            type="button"
            className="btn-secondary btn-color"
            onClick={handleSkip}
            disabled={saving}
          >
            {t("botChannels.onboarding.common.skip", "Skip for now")}
          </button>
        </div>

        {/* Meta permissions dialog */}
        {metaPermissionsChannel && (
          <div className="meta-permissions-modal-backdrop">
            <div className="meta-permissions-modal">
              <div className="meta-permissions-modal-header">
                <h2>
                  {metaPermissionsChannel === "FACEBOOK"
                    ? t("botChannels.meta.permissionsDialog.titleFacebook")
                    : t("botChannels.meta.permissionsDialog.titleInstagram")}
                </h2>
                <button
                  type="button"
                  className="btn-ghost small"
                  onClick={closeMetaPermissionsDialog}
                >
                  {t("botChannels.actions.cancel")}
                </button>
              </div>

              <div className="meta-permissions-modal-body">
                <p className="muted">
                  {metaPermissionsChannel === "FACEBOOK"
                    ? t("botChannels.meta.permissionsDialog.introFacebook")
                    : t("botChannels.meta.permissionsDialog.introInstagram")}
                </p>

                <p className="meta-permissions-list-title">
                  {t("botChannels.meta.permissionsDialog.listTitle")}
                </p>

                {metaPermissionsChannel === "FACEBOOK" ? (
                  <ul className="meta-permissions-list">
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.pagesShowList"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.pagesMessaging"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.pagesManageMetadata"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.businessManagement"
                      )}
                    </li>
                  </ul>
                ) : (
                  <ul className="meta-permissions-list">
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.pagesShowList"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.instagramBasic"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.instagramManageMessages"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.pagesManageMetadata"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.businessManagement"
                      )}
                    </li>
                  </ul>
                )}

                <label
                  className="checkbox-inline"
                  style={{ marginTop: "0.75rem" }}
                >
                  <input
                    type="checkbox"
                    checked={metaPermissionsAccepted}
                    onChange={(e) =>
                      setMetaPermissionsAccepted(e.target.checked)
                    }
                  />
                  <span>
                    {t("botChannels.meta.permissionsDialog.checkboxLabel")}
                  </span>
                </label>

                <p className="muted small" style={{ marginTop: "0.5rem" }}>
                  {t("botChannels.meta.permissionsDialog.footerNote")}
                </p>
              </div>

              <div className="meta-permissions-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeMetaPermissionsDialog}
                >
                  {t("botChannels.actions.cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    !metaPermissionsAccepted || metaPermissionsSubmitting
                  }
                  onClick={handleConfirmMetaPermissions}
                >
                  {metaPermissionsChannel === "FACEBOOK"
                    ? t(
                        "botChannels.meta.permissionsDialog.continueFacebook"
                      )
                    : t(
                        "botChannels.meta.permissionsDialog.continueInstagram"
                      )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Meta session page selection modal */}
        {metaSession && metaSelectModalOpen && (
          <div className="meta-page-select-modal-backdrop">
            <div className="meta-page-select-modal">
              <div className="meta-page-select-modal-header">
                <h2>
                  {metaSession.channelType === "FACEBOOK"
                    ? t("botChannels.meta.selectModal.titleFacebook")
                    : t("botChannels.meta.selectModal.titleInstagram")}
                </h2>
                <button
                  type="button"
                  className="btn-ghost small"
                  onClick={closeMetaSelectModal}
                >
                  {t("botChannels.actions.cancel")}
                </button>
              </div>

              <div className="meta-page-select-modal-body">
                <p className="muted">
                  {metaSession.channelType === "FACEBOOK"
                    ? t("botChannels.meta.selectModal.introFacebook")
                    : t("botChannels.meta.selectModal.introInstagram")}
                </p>

                {metaSessionLoading && (
                  <p className="muted">
                    {t("botChannels.meta.loadingPages")}
                  </p>
                )}
                {metaSessionError && (
                  <div className="form-error onboarding-form-error-txt">
                    {t("botChannels.meta.sessionLoadFailed")}:{" "}
                    {metaSessionError}
                  </div>
                )}

                {!metaSessionLoading &&
                  !metaSessionError &&
                  (() => {
                    const eligiblePages = metaSession.pages.filter((p) =>
                      metaSession.channelType === "INSTAGRAM"
                        ? !!p.instagramBusinessId
                        : true
                    );

                    const personalPages = eligiblePages.filter(
                      (p) => !p.isBusinessManaged
                    );
                    const businessPages = eligiblePages.filter(
                      (p) => p.isBusinessManaged
                    );

                    const hasAnyPages =
                      metaSession.pages && metaSession.pages.length > 0;
                    const isWeirdInstagramState =
                      metaSession.channelType === "INSTAGRAM" &&
                      hasAnyPages &&
                      eligiblePages.length === 0;

                    if (eligiblePages.length === 0) {
                      return (
                        <>
                          <p className="muted">
                            {t("botChannels.meta.selectModal.noPages")}
                          </p>

                          {isWeirdInstagramState && (
                            <p
                              className="muted small"
                              style={{ marginTop: "0.5rem" }}
                            >
                              {t(
                                "botChannels.meta.selectModal.editPreviousSettingsHint"
                              )}
                            </p>
                          )}
                        </>
                      );
                    }

                    return (
                      <>
                        {personalPages.length > 0 && (
                          <>
                            <p className="meta-page-select-group-title">
                              {t(
                                "botChannels.meta.selectModal.personalGroupTitle"
                              )}
                            </p>
                            <div className="meta-page-select-list">
                              {personalPages.map((p) => (
                                <label
                                  key={p.id}
                                  className={
                                    "meta-page-select-item" +
                                    (metaSelectedPageId === p.id
                                      ? " selected"
                                      : "")
                                  }
                                >
                                  <input
                                    type="radio"
                                    name="meta-page-select"
                                    value={p.id}
                                    checked={metaSelectedPageId === p.id}
                                    onChange={() =>
                                      setMetaSelectedPageId(p.id)
                                    }
                                  />
                                  <span className="meta-page-select-name">
                                    {p.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}

                        {businessPages.length > 0 && (
                          <>
                            <p className="meta-page-select-group-title">
                              {t(
                                "botChannels.meta.selectModal.businessGroupTitle"
                              )}
                            </p>
                            <div className="meta-page-select-list">
                              {businessPages.map((p) => (
                                <label
                                  key={p.id}
                                  className={
                                    "meta-page-select-item" +
                                    (metaSelectedPageId === p.id
                                      ? " selected"
                                      : "")
                                  }
                                >
                                  <input
                                    type="radio"
                                    name="meta-page-select"
                                    value={p.id}
                                    checked={metaSelectedPageId === p.id}
                                    onChange={() =>
                                      setMetaSelectedPageId(p.id)
                                    }
                                  />
                                  <span className="meta-page-select-name">
                                    {p.name}
                                    {p.businessName
                                      ? ` (${p.businessName})`
                                      : ""}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
              </div>

              <div className="meta-page-select-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeMetaSelectModal}
                >
                  {t("botChannels.actions.cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!metaSelectedPageId || metaAttachLoading}
                  onClick={handleAttachMetaSession}
                >
                  {metaAttachLoading
                    ? t("botChannels.actions.connecting")
                    : t("botChannels.actions.connectSelected")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp PIN + number modal (reuses meta modal styles) */}
        {waSession && (
          <div className="meta-page-select-modal-backdrop">
            <div className="meta-page-select-modal">
              <div className="meta-page-select-modal-header">
                <h2>
                  {t(
                    "botChannels.whatsapp.pinModal.title",
                    "Connect WhatsApp Business"
                  )}
                </h2>
                <button
                  type="button"
                  className="btn-ghost small"
                  onClick={() => {
                    setWaSession(null);
                    setWaSelectedNumberId("");
                    setWaRegistrationPin("");
                  }}
                >
                  {t("botChannels.actions.cancel")}
                </button>
              </div>

              <div className="meta-page-select-modal-body">
                <p className="muted">
                  {t(
                    "botChannels.whatsapp.pinModal.intro",
                    "Select the WhatsApp Business phone number you want to connect and enter the two-step verification PIN for that number."
                  )}
                </p>

                <p className="muted small" style={{ marginTop: "0.5rem" }}>
                  {t(
                    "botChannels.whatsapp.pinModal.whatIsPin",
                    "This PIN is the 6-digit two-step verification PIN configured for this phone number in WhatsApp Manager, not your Google Authenticator code or login 2FA."
                  )}
                </p>

                <ol className="muted small" style={{ marginTop: "0.75rem" }}>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step1",
                      "Open WhatsApp Manager and go to Account tools → Phone numbers."
                    )}
                  </li>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step2",
                      "Click the gear icon next to your phone number and open Two-step verification."
                    )}
                  </li>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step3",
                      "If you don't remember the PIN, turn off two-step verification from there (you'll get an email), then set a new 6-digit PIN."
                    )}
                  </li>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step4",
                      "Come back here and enter that exact 6-digit PIN."
                    )}
                  </li>
                </ol>

                <label className="form-field" style={{ marginTop: "1rem" }}>
                  <span>{t("botChannels.whatsapp.numberLabel")}</span>
                  <select
                    value={waSelectedNumberId}
                    onChange={(e) => setWaSelectedNumberId(e.target.value)}
                  >
                    <option value="">
                      {t("botChannels.whatsapp.selectPlaceholder")}
                    </option>
                    {waSession.numbers.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.displayPhoneNumber || n.id}
                        {n.verifiedName ? ` – ${n.verifiedName}` : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field">
                  <span>
                    {t(
                      "botChannels.whatsapp.pinLabel",
                      "WhatsApp two-step verification PIN"
                    )}
                  </span>
                  <input
                    type="text"
                    maxLength={6}
                    value={waRegistrationPin}
                    onChange={(e) =>
                      setWaRegistrationPin(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder={t(
                      "botChannels.whatsapp.pinPlaceholder",
                      "6-digit PIN set in WhatsApp Manager"
                    )}
                  />
                  <p className="muted small" style={{ marginTop: "0.25rem" }}>
                    {t(
                      "botChannels.whatsapp.pinModal.pinHint",
                      "Enter exactly the same 6-digit PIN you see under Two-step verification for this phone number in WhatsApp Manager."
                    )}
                  </p>
                </label>
              </div>

              <div className="meta-page-select-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setWaSession(null);
                    setWaSelectedNumberId("");
                    setWaRegistrationPin("");
                  }}
                >
                  {t("botChannels.actions.cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    !waSelectedNumberId || !isValidWaPin || waAttachLoading
                  }
                  onClick={handleAttachWhatsappSession}
                >
                  {waAttachLoading
                    ? t("botChannels.actions.connecting")
                    : t("botChannels.actions.connectSelected")}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && <div className="form-error">{error}</div>}
        {loading && <p className="muted">{t("botChannels.loading")}</p>}

        {!loading && (
          <div className="connect-grid">
            {/* META INBOX CARD */}
            <section className="connect-card">
              <header className="connect-card-header">
                <div>
                  <h2>
                    {t(
                      "botChannels.onboarding.channels.meta.title",
                      "Meta inbox (Facebook & Instagram)"
                    )}
                  </h2>
                  <p className="connect-card-subtitle">
                    {t(
                      "botChannels.onboarding.channels.meta.subtitle",
                      "Connect your Facebook page and Instagram Business account so Coslo can reply to messages automatically."
                    )}
                  </p>
                </div>
                <div className="connect-card-toggle">
                  <label className="booking-toggle">
                    <input
                      type="checkbox"
                      checked={metaEnabled}
                      onChange={(e) => setMetaEnabled(e.target.checked)}
                    />
                    <span className="booking-toggle-slider" />
                    <span className="booking-toggle-label">
                      {metaEnabled
                        ? t(
                            "botChannels.onboarding.channels.meta.toggleOn",
                            "Meta inbox is ON"
                          )
                        : t(
                            "botChannels.onboarding.channels.meta.toggleOff",
                            "Meta inbox is OFF"
                          )}
                    </span>
                  </label>
                  {!metaEnabled && (
                    <p className="muted small">
                      {t(
                        "botChannels.onboarding.channels.meta.toggleOffHint",
                        "Keep Meta inbox disabled if you don't want Coslo to reply on Facebook or Instagram for this bot."
                      )}
                    </p>
                  )}
                </div>
              </header>

              {metaEnabled ? (
                <>
                  {/* Facebook row */}
                  <div className="connect-channel">
                    <div className="connect-channel-main">
                      <div className="connect-channel-icon connect-channel-icon-facebook">
                        f
                      </div>
                      <div className="connect-channel-text">
                        <div className="connect-channel-title-row">
                          <span className="connect-channel-title">
                            {t("botChannels.labels.facebookPage")}
                          </span>
                          {renderChannelStatusBadge(facebookChannel)}
                        </div>
                        <p className="connect-channel-description">
                          {t("botChannels.connectSocial.facebookDesc")}
                        </p>
                        {facebookChannel && (
                          <p className="connect-channel-meta">
                            {t("botChannels.connectSocial.connectedPage")}{" "}
                            <strong>
                              {(facebookChannel.meta as any)?.pageName ||
                                t("botChannels.meta.connectedViaMeta")}
                            </strong>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="connect-channel-actions">
                      {!fbStatus.isConnected && (
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={() => openMetaPermissionsDialog("FACEBOOK")}
                        >
                          {facebookChannel
                            ? t("botChannels.actions.reconnectFacebook")
                            : t("botChannels.actions.connectFacebook")}
                        </button>
                      )}
                      {facebookChannel && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm btn-color"
                          onClick={() => handleDelete(facebookChannel.id)}
                        >
                          {t("botChannels.actions.disconnect")}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Instagram row */}
                  <div className="connect-channel">
                    <div className="connect-channel-main">
                      <div className="connect-channel-icon connect-channel-icon-instagram">
                        ig
                      </div>
                      <div className="connect-channel-text">
                        <div className="connect-channel-title-row">
                          <span className="connect-channel-title">
                            {t("botChannels.labels.instagramBusiness")}
                          </span>
                          {renderChannelStatusBadge(instagramChannel)}
                        </div>
                        <p className="connect-channel-description">
                          {t("botChannels.connectSocial.instagramDesc")}
                        </p>
                        {instagramChannel && (
                          <p className="connect-channel-meta">
                            {t(
                              "botChannels.connectSocial.connectedAccount"
                            )}{" "}
                            <strong>
                              {(() => {
                                const meta =
                                  (instagramChannel.meta as any) || {};
                                return (
                                  meta.igUsername ||
                                  meta.igName ||
                                  meta.pageName ||
                                  t("botChannels.meta.connectedViaMeta")
                                );
                              })()}
                            </strong>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="connect-channel-actions">
                      {!igStatus.isConnected && (
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={() =>
                            openMetaPermissionsDialog("INSTAGRAM")
                          }
                        >
                          {instagramChannel
                            ? t("botChannels.actions.reconnectInstagram")
                            : t("botChannels.actions.connectInstagram")}
                        </button>
                      )}
                      {instagramChannel && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm btn-color"
                          onClick={() => handleDelete(instagramChannel.id)}
                        >
                          {t("botChannels.actions.disconnect")}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <p className="muted small" style={{ marginTop: "0.75rem" }}>
                  {t(
                    "botChannels.onboarding.channels.meta.disabledBody",
                    "Meta inbox is disabled for this bot. Turn it on above if you want to connect Facebook or Instagram."
                  )}
                </p>
              )}
            </section>

            {/* WHATSAPP CARD */}
            <section className="connect-card">
              <header className="connect-card-header">
                <div>
                  <h2>
                    {t(
                      "botChannels.onboarding.channels.whatsapp.title",
                      "WhatsApp Business (embedded signup)"
                    )}
                  </h2>
                  <p className="connect-card-subtitle">
                    {t(
                      "botChannels.onboarding.channels.whatsapp.subtitle",
                      "Connect a WhatsApp Business number using the official Meta embedded signup flow."
                    )}
                  </p>
                </div>
                <div className="connect-card-toggle">
                  <label className="booking-toggle">
                    <input
                      type="checkbox"
                      checked={whatsappEnabled}
                      onChange={(e) => setWhatsappEnabled(e.target.checked)}
                    />
                    <span className="booking-toggle-slider" />
                    <span className="booking-toggle-label">
                      {whatsappEnabled
                        ? t(
                            "botChannels.onboarding.channels.whatsapp.toggleOn",
                            "WhatsApp is ON"
                          )
                        : t(
                            "botChannels.onboarding.channels.whatsapp.toggleOff",
                            "WhatsApp is OFF"
                          )}
                    </span>
                  </label>
                  {!whatsappEnabled && (
                    <p className="muted small">
                      {t(
                        "botChannels.onboarding.channels.whatsapp.toggleOffHint",
                        "Keep WhatsApp disabled if you don't want Coslo to send messages on WhatsApp for this bot."
                      )}
                    </p>
                  )}
                </div>
              </header>

              {whatsappEnabled ? (
                <div className="connect-channel">
                  <div className="connect-channel-main">
                    <div className="connect-channel-icon connect-channel-icon-whatsapp">
                      wa
                    </div>
                    <div className="connect-channel-text">
                      <div className="connect-channel-title-row">
                        <span className="connect-channel-title">
                          {t("botChannels.labels.whatsappBusiness")}
                        </span>
                        {renderChannelStatusBadge(whatsappChannel)}
                      </div>
                      <p className="connect-channel-description">
                        {t("botChannels.connectSocial.whatsappDesc")}
                      </p>
                      {whatsappChannel && (
                        <p className="connect-channel-meta">
                          {t("botChannels.connectSocial.connectedNumber")}{" "}
                          <strong>
                            {(() => {
                              const meta = (whatsappChannel.meta as any) || {};
                              return (
                                meta.displayPhoneNumber ||
                                meta.display_phone_number ||
                                whatsappChannel.externalId
                              );
                            })()}
                          </strong>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="connect-channel-actions">
                    {!waStatus.isConnected && (
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={handleConnectWhatsApp}
                        disabled={waConnecting}
                      >
                        {waConnecting
                          ? t("botChannels.actions.connectingWhatsapp")
                          : whatsappChannel
                          ? t("botChannels.actions.reconnectWhatsapp")
                          : t("botChannels.actions.connectWhatsapp")}
                      </button>
                    )}
                    {whatsappChannel && (
                      <button
                        type="button"
                        className="btn-secondary btn-sm btn-color"
                        onClick={() => handleDelete(whatsappChannel.id)}
                      >
                        {t("botChannels.actions.disconnect")}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="muted small" style={{ marginTop: "0.75rem" }}>
                  {t(
                    "botChannels.onboarding.channels.whatsapp.disabledBody",
                    "WhatsApp is disabled for this bot. Turn it on above if you want to connect a number."
                  )}
                </p>
              )}
            </section>
          </div>
        )}

        {/* Wizard navigation */}
        <div
          className="onboarding-form-actions"
          style={{ marginTop: "1.2rem" }}
        >
          <button
            className="btn-secondary btn-color"
            type="button"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            {t("common.back", "Back")}
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={handleNext}
            disabled={!canContinue || saving}
          >
            {t(
              "botChannels.onboarding.channels.next",
              "Continue to bookings"
            )}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingChannelsPage;
